import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
  Autocomplete,
} from "@mui/material";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as yup from "yup";
import {
  FilledButton,
  StyledLoadingButton,
} from "../../customComponents/styled/styledButtons";
import {
  useCreateSubjectMutation,
  useGetSubjectsQuery,
} from "./subjectsApiSlice";
import toast from "../../../helpers/toast";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import {
  useAddSubjectToClassroomMutation,
  useGetClassesQuery,
} from "../classroom/classApiSlice";
import SimilarSubjectDialog from "./SimilarSubjectDialog";

const validationSchema = yup.object({
  subjectName: yup.string().required("Subject is required"),
  description: yup.string(),
  classes: yup.array().of(
    yup.object().shape({
      class: yup.string().required("Required"),
      teacher: yup.string().required("Required"),
      // type: yup.string().required("Required"),
    }),
  ),
});

function NewSubjectForm({ setFocusedSubject, setOpenEdit }) {
  const [similarSubjectModal, setSimilarSubjectModal] = React.useState(null);
  const [pendingSubmitValues, setPendingSubmitValues] = React.useState(null);

  const { data: classrooms } = useGetClassesQuery("all"); //to get updated list of classrooms
  const [teacherSearch, setTeacherSearch] = React.useState("");
  const { data: teachersData } = useGetTeachersPaginatedQuery({
    page: 1,
    per_page: 10,
    q: teacherSearch,
  });
  const teachers = teachersData?.teachers ?? [];
  const subjectTypes = ["score", "grade", "comment"];

  const [createSubject, { isLoading }] = useCreateSubjectMutation();
  const [addSubjectToClass, { isLoading: addSubjectToClassLoading }] =
    useAddSubjectToClassroomMutation();

  const { data: existingSubjects } = useGetSubjectsQuery("all");

  const checkSimilarity = (input, existingList) => {
    if (!existingList) return null;
    const normalizedInput = input.trim().toLowerCase();

    const aliases = {
      maths: "mathematics",
      math: "mathematics",
      irs: "islamic religious studies",
      "islamic religous studies": "islamic religious studies",
      crs: "christian religious studies",
      phe: "physical and health education",
      agric: "agricultural science",
      govt: "government",
      lit: "literature",
      "lit in english": "literature in english",
    };

    const checkVal = aliases[normalizedInput] || normalizedInput;

    return existingList.find((sub) => {
      const subName = sub.name.trim().toLowerCase();
      const subAlias = aliases[subName] || subName;

      if (subAlias === checkVal) return true;
      if (
        checkVal.length > 3 &&
        (subAlias.includes(checkVal) || checkVal.includes(subAlias))
      )
        return true;

      return false;
    });
  };

  const formik = useFormik({
    initialValues: {
      subjectName: "",
      description: "",
      // classes: [{ class: "", teacher: "", type: "" }],
      classes: [{ class: "", teacher: "" }],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const similarSubject = checkSimilarity(values.subjectName, existingSubjects);
      if (similarSubject) {
        setSimilarSubjectModal(similarSubject);
        setPendingSubmitValues(values);
        return;
      }
      await processSubmission(values);
    },
  });

  const processSubmission = async (values) => {
    try {
      const body = {
        name: values.subjectName,
        description: values.description,
      };
      const response = await createSubject(body).unwrap();
      if (response.id) {
        toast.success("Subject created successfully");
        //add each subject to each class
        values.classes.forEach(async (classroom) => {
          const subjectToClassBody = {
            class_Id: classroom.class,
            teacher_id: classroom.teacher,
            subject_id: response.id,
          };
          const res = await addSubjectToClass(subjectToClassBody).unwrap();
          formik.resetForm();
          if (res.message) {
            toast.success("Subject added to classes successfully");
          }
        });
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
    setSimilarSubjectModal(null);
    setPendingSubmitValues(null);
  };

  return (
    <>
      <Box className="paperHeader">
        <Typography variant="formHeading">New Subject Form</Typography>
      </Box>
      <Box className="paperBody">
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={3}>
            <Grid item xs={12} md={12}>
              <FormControl
                fullWidth
                error={formik.touched.subjectName && formik.errors.subjectName}
              >
                <Typography variant="formSubHeading">Subject Name</Typography>
                <StyledTextField
                  autoFocus
                  variant="outlined"
                  id="subjectName"
                  name="subjectName"
                  value={formik.values.subjectName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.subjectName &&
                    Boolean(formik.errors.subjectName)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.subjectName && formik.errors.subjectName}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* <Grid item xs={12} md={8}>
              <FormControl
                fullWidth
                error={formik.touched.description && formik.errors.description}
              >
                <Typography htmlFor="description" variant="formSubHeading">
                  description
                </Typography>
                <StyledTextField
                  id="description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
                <FormHelperText variant="error">
                  {formik.touched.description && formik.errors.description}
                </FormHelperText>
              </FormControl>
            </Grid> */}

            {/* beggining second row inputs */}

            <FormikProvider value={formik}>
              <FieldArray
                name="classes"
                render={(arrayHelpers) => {
                  const classes = formik.values.classes;
                  return (
                    <>
                      {classes && classes.length > 0
                        ? classes.map((row, index) => (
                            <React.Fragment key={index}>
                              {/* <Grid item xs={12} md={3}>
                                <FormControl
                                  error={
                                    formik.touched.classes?.[index]?.type &&
                                    formik.errors.classes?.[index]?.type
                                  }
                                  fullWidth
                                >
                                  <Typography
                                    htmlFor={`type-${index}`}
                                    variant="formSubHeading"
                                  >
                                    Type
                                  </Typography>
                                  <StyledSelect
                                    displayEmpty
                                    id={`type-${index}`}
                                    name={`classes.${index}.type`}
                                    value={row.type}
                                    onChange={formik.handleChange}
                                    error={
                                      formik.touched.classes?.[index]?.type &&
                                      Boolean(
                                        formik.errors.classes?.[index]?.type,
                                      )
                                    }
                                  >
                                    <MenuItem value="" disabled>
                                      <em>select type</em>
                                    </MenuItem>
                                    {subjectTypes?.map((type) => (
                                      <MenuItem key={type} value={type}>
                                        {type}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                  <FormHelperText variant="error">
                                    {formik.touched.classes?.[index]?.type &&
                                      formik.errors.classes?.[index]?.type}
                                  </FormHelperText>
                                </FormControl>
                              </Grid> */}
                              <Grid item xs={12} md={5}>
                                <FormControl
                                  error={
                                    formik.touched.classes?.[index]?.class &&
                                    formik.errors.classes?.[index]?.class
                                  }
                                  fullWidth
                                >
                                  <Typography
                                    htmlFor="class"
                                    variant="formSubHeading"
                                  >
                                    class
                                  </Typography>
                                  <StyledSelect
                                    displayEmpty
                                    id="class"
                                    name={`classes.${index}.class`}
                                    value={row.class}
                                    onChange={formik.handleChange}
                                    error={
                                      formik.touched.classes?.[index]?.class &&
                                      Boolean(
                                        formik.errors.classes?.[index]?.class,
                                      )
                                    }
                                  >
                                    <MenuItem value="" disabled>
                                      <em>select classroom</em>
                                    </MenuItem>
                                    {classrooms?.map((classroom) => (
                                      <MenuItem
                                        key={classroom.id}
                                        value={classroom.id}
                                      >
                                        {classroom.name}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                  <FormHelperText variant="error">
                                    {formik.touched.classes?.[index]?.class &&
                                      formik.errors.classes?.[index]?.class}
                                  </FormHelperText>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} md={5}>
                                <FormControl
                                  error={
                                    formik.touched.classes?.[index]?.teacher &&
                                    formik.errors.classes?.[index]?.teacher
                                  }
                                  fullWidth
                                >
                                  <Typography
                                    htmlFor="subjectType"
                                    variant="formSubHeading"
                                  >
                                    teacher
                                  </Typography>
                                  <Autocomplete
                                    options={teachers}
                                    getOptionLabel={(option) =>
                                      option
                                        ? `${option.first_name} ${option.last_name}`
                                        : ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option.user_id === value.user_id
                                    }
                                    value={
                                      teachers.find(
                                        (t) => t.user_id === row.teacher,
                                      ) || null
                                    }
                                    onChange={(_, value) =>
                                      formik.setFieldValue(
                                        `classes.${index}.teacher`,
                                        value?.user_id || "",
                                      )
                                    }
                                    onInputChange={(_, value, reason) => {
                                      if (reason === "input")
                                        setTeacherSearch(value);
                                    }}
                                    filterOptions={(x) => x}
                                    renderInput={(params) => (
                                      <StyledTextField
                                        {...params}
                                        placeholder="Search teacher..."
                                        error={
                                          formik.touched.classes?.[index]
                                            ?.teacher &&
                                          Boolean(
                                            formik.errors.classes?.[index]
                                              ?.teacher,
                                          )
                                        }
                                      />
                                    )}
                                  />
                                  <FormHelperText variant="error">
                                    {formik.touched.classes?.[index]?.teacher &&
                                      formik.errors.classes?.[index]?.teacher}
                                  </FormHelperText>
                                </FormControl>
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                md={2}
                                sx={{
                                  display: {
                                    md: "flex",
                                  },
                                  alignItems: "flex-end",
                                  justifyContent: "flex-end",
                                  mb: 0.5,
                                }}
                              >
                                {index === 0 ? (
                                  <FilledButton
                                    onClick={() =>
                                      arrayHelpers.push({
                                        class: "",
                                        teacher: "",
                                        // assTeacher: "",
                                      })
                                    }
                                  >
                                    +
                                  </FilledButton>
                                ) : (
                                  <FilledButton
                                    onClick={() => arrayHelpers.remove(index)}
                                  >
                                    x
                                  </FilledButton>
                                )}
                              </Grid>
                            </React.Fragment>
                          ))
                        : null}
                    </>
                  );
                }}
              />
            </FormikProvider>

            <Grid item xs={12} md={12} textAlign={"right"}>
              <StyledLoadingButton
                type="submit"
                loading={isLoading || addSubjectToClassLoading}
              >
                SUBMIT
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <SimilarSubjectDialog
        open={Boolean(similarSubjectModal)}
        onClose={() => setSimilarSubjectModal(null)}
        similarSubject={similarSubjectModal}
        onUpdateExisting={() => {
          setSimilarSubjectModal(null);
          setFocusedSubject(similarSubjectModal);
          setOpenEdit(true);
        }}
        onCreateNew={() => processSubmission(pendingSubmitValues)}
      />
    </>
  );
}

export default NewSubjectForm;
